import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/text-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/filled-button.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { sharedStyles } from "../styles/shared";
import { icon } from "../lib/icons";
import { errorMessage } from "../lib/dom";
import { toast } from "../lib/toast";
import { navigate } from "../lib/navigation";
import {
  provisioning,
  type Prospect,
  type EbsiOnboardingStatus,
} from "../lib/provisioning";

// The Trusted Issuer trust-chain track (ported from ebsi-onboarding-html-only.html):
// 8 steps across a Business and a Technical phase. Each step lists the real EBSI
// artifacts it produces; the console reflects live onboarding state rather than
// collecting data that would go nowhere.
interface Step {
  n: number;
  phase: "Business" | "Technical";
  title: string;
  desc: string;
  artifacts: string[];
}

const TI_STEPS: Step[] = [
  {
    n: 1,
    phase: "Business",
    title: "Organisation Profile",
    desc: "Legal identity + the credential types this issuer will attest.",
    artifacts: ["Legal name", "VAT number", "Contact", "Credential types"],
  },
  {
    n: 2,
    phase: "Technical",
    title: "Set Up Organisation Wallet",
    desc: "Provision an EBSI-compatible wallet that will hold the issuer keys.",
    artifacts: ["Wallet provider", "Wallet Service URL"],
  },
  {
    n: 3,
    phase: "Business",
    title: "Identify Your Accrediting Organisation",
    desc: "The TAO (or Root TAO) that will accredit this issuer.",
    artifacts: ["Accrediting org name", "Accrediting DID", "Contact"],
  },
  {
    n: 4,
    phase: "Technical",
    title: "Generate DID & DID Document",
    desc: "Client-side keys: ES256 (assertion/auth) + ES256K (capability).",
    artifacts: ["did:ebsi", "P-256 JWK", "secp256k1 JWK"],
  },
  {
    n: 5,
    phase: "Technical",
    title: "Request Authorisation to Onboard",
    desc: "Obtain a VerifiableAuthorisationToOnboard from the accrediting org.",
    artifacts: ["Authorisation credential"],
  },
  {
    n: 6,
    phase: "Technical",
    title: "Register DID in EBSI DID Registry",
    desc: "Anchor the DID document on-ledger (signed with ES256K).",
    artifacts: ["insertDidDocument tx", "addVerificationMethod tx"],
  },
  {
    n: 7,
    phase: "Technical",
    title: "Receive Verifiable Accreditation to Attest",
    desc: "The accrediting org issues the accreditation for this issuer.",
    artifacts: ["Accreditation credential"],
  },
  {
    n: 8,
    phase: "Technical",
    title: "Register in Trusted Issuers Registry",
    desc: "Write the accreditation attribute to TIR — the issuer is now live.",
    artifacts: ["TIR tx", "reservedAttributeId"],
  },
];

// EBSI onboarding state machine → how many TI steps are complete.
const STATE_PROGRESS: Record<EbsiOnboardingStatus["state"], number> = {
  application_received: 1,
  kyb_in_progress: 1,
  kyb_completed: 3,
  kyb_failed: 1,
  pending_review: 3,
  approved: 3,
  did_created: 6,
  accreditation_issued: 7,
  active: 8,
  rejected: 0,
  revoked: 0,
};

const STATE_LABEL: Record<EbsiOnboardingStatus["state"], string> = {
  application_received: "Application received",
  kyb_in_progress: "KYB in progress",
  kyb_completed: "KYB completed",
  kyb_failed: "KYB failed",
  pending_review: "Pending review",
  approved: "Approved",
  did_created: "DID created",
  accreditation_issued: "Accreditation issued",
  active: "Active — Trusted Issuer",
  rejected: "Rejected",
  revoked: "Revoked",
};

@customElement("ebsi-onboarding-page")
export class EbsiOnboardingPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .cols {
        display: grid;
        grid-template-columns: 1fr;
        gap: 20px;
        align-items: start;
      }
      @media (min-width: 840px) {
        .cols {
          grid-template-columns: minmax(260px, 300px) 1fr;
        }
      }
      .wallet .row {
        padding: 12px 20px;
        border-top: 1px solid var(--md-sys-color-outline-variant);
      }
      .wallet .wl {
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
        margin-bottom: 4px;
      }
      .codefield {
        font-family: var(--font-mono, monospace);
        font-size: 12px;
        word-break: break-all;
        background: var(--md-sys-color-surface-container-high);
        border-radius: 8px;
        padding: 8px 10px;
      }
      .codefield .empty {
        color: var(--md-sys-color-outline);
        font-family: var(--font-plain, sans-serif);
      }
      .netrow {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
      }
      .netdot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: var(--md-sys-color-primary);
        display: inline-block;
      }
      .phase {
        font: 500 11px/1 var(--font-plain, sans-serif);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--md-sys-color-on-surface-variant);
        padding: 14px 20px 6px;
      }
      .step {
        display: flex;
        gap: 14px;
        align-items: flex-start;
        padding: 12px 20px;
        border-top: 1px solid var(--md-sys-color-outline-variant);
      }
      .step__num {
        width: 28px;
        height: 28px;
        flex: none;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font: 500 13px/1 var(--font-plain, sans-serif);
        background: var(--md-sys-color-surface-container-high);
        color: var(--md-sys-color-on-surface-variant);
        --md-icon-size: 18px;
      }
      .step--done .step__num {
        background: var(--md-sys-color-primary);
        color: var(--md-sys-color-on-primary);
      }
      .step--current .step__num {
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
      }
      .step__title {
        font: 500 14px/1.3 var(--font-plain, sans-serif);
      }
      .step--current .step__title {
        color: var(--md-sys-color-primary);
      }
      .step__desc {
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
        margin-top: 2px;
      }
      .artifacts {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 6px;
      }
      .artifacts .chip {
        font: 400 11px/1 var(--font-mono, monospace);
        padding: 4px 8px;
        border-radius: 6px;
        background: var(--md-sys-color-surface-container-high);
        color: var(--md-sys-color-on-surface-variant);
      }
      .head-actions {
        display: flex;
        gap: 8px;
        align-items: center;
      }
    `,
  ];

  @property({ type: String }) prospectId = "";

  @state() private loading = true;
  @state() private error = "";
  @state() private approving = false;
  @state() private prospect: Prospect | null = null;
  @state() private status: EbsiOnboardingStatus | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      this.prospect = await provisioning.prospect(this.prospectId);
      if (!this.prospect) throw new Error("Prospect not found.");
      this.status = await provisioning.ebsiStatus(this.prospectId);
    } catch (err) {
      this.error = errorMessage(err, "Failed to load EBSI onboarding.");
    } finally {
      this.loading = false;
    }
  }

  private get completed(): number {
    return this.status ? STATE_PROGRESS[this.status.state] : 0;
  }

  // Reviewer approval: on the EBSI platform this approves the pending
  // application, registers the DID and drives it to active (Authentrace acts as
  // the accrediting reviewer in this platform-operated setup).
  private async approve(): Promise<void> {
    this.approving = true;
    this.error = "";
    try {
      await provisioning.approveEbsi(this.prospectId);
      toast("Onboarding approved — DID issued");
      await this.load();
    } catch (err) {
      this.error = errorMessage(err, "Could not approve onboarding.");
    } finally {
      this.approving = false;
    }
  }

  render() {
    return html`
      <app-shell active="/provisioning">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>EBSI Onboarding — Trusted Issuer</h1>
              <p>
                ${this.prospect
                  ? `Trust-chain onboarding for ${this.prospect.legalName}.`
                  : "Trust-chain onboarding."}
              </p>
            </div>
            <div class="head-actions">
              <md-outlined-button @click=${() => void navigate("/provisioning")}>
                <span slot="icon">${icon("arrow_back")}</span>
                Back
              </md-outlined-button>
              <md-outlined-button @click=${() => void this.load()}>
                <span slot="icon">${icon("history")}</span>
                Refresh
              </md-outlined-button>
              ${this.status?.state === "pending_review"
                ? html`<md-filled-button ?disabled=${this.approving} @click=${() => void this.approve()}>
                    <span slot="icon">${icon("verified")}</span>
                    ${this.approving ? "Approving…" : "Approve onboarding"}
                  </md-filled-button>`
                : null}
            </div>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.renderConsole()}
        </div>
      </app-shell>
    `;
  }

  private renderConsole() {
    const status = this.status!;
    const done = this.completed;
    const pct = Math.round((done / TI_STEPS.length) * 100);
    const failed = status.state === "kyb_failed" || status.state === "rejected";
    return html`
      <div class="stats">
        <div class="stat">
          <div class="n">${done}<small> / 8</small></div>
          <div class="l">Steps completed</div>
        </div>
        <div class="stat">
          <div class="n">${pct}%</div>
          <div class="l">Progress</div>
        </div>
        <div class="stat">
          <div class="n" style="font-size:1.25rem">
            ${status.applicantDid ? "Created" : "Not created"}
          </div>
          <div class="l">DID status</div>
        </div>
        <div class="stat">
          <div class="n" style="font-size:1.25rem">
            ${status.state === "active"
              ? html`<span style="color:var(--md-sys-color-primary)">Live</span>`
              : failed
                ? html`<span style="color:var(--md-sys-color-error)">Blocked</span>`
                : "In progress"}
          </div>
          <div class="l">${STATE_LABEL[status.state]}</div>
        </div>
      </div>

      <div class="cols">
        <div class="card card--flush wallet">
          <div class="cardhead"><h2>Wallet</h2></div>
          <div class="row">
            <div class="wl">Applicant DID</div>
            <div class="codefield">
              ${status.applicantDid
                ? status.applicantDid
                : html`<span class="empty">Not yet generated</span>`}
            </div>
          </div>
          <div class="row">
            <div class="wl">Onboarding ID</div>
            <div class="codefield">${status.id}</div>
          </div>
          ${status.siren
            ? html`<div class="row">
                <div class="wl">SIREN</div>
                <div class="codefield">${status.siren}</div>
              </div>`
            : null}
          <div class="row">
            <span class="netrow"><span class="netdot"></span>conformance</span>
          </div>
        </div>

        <div class="card card--flush">
          <div class="cardhead">
            <h2>Trust-chain steps</h2>
            <span class="badge ${failed ? "badge--bad" : done === 8 ? "badge--ok" : "badge--gi"}">
              ${done}/8
            </span>
          </div>
          ${this.renderSteps(done, failed)}
          ${status.state === "pending_review"
            ? html`<div class="row" style="padding:14px 20px;border-top:1px solid var(--md-sys-color-outline-variant)">
                <span class="muted" style="font-size:12px">
                  KYB passed — awaiting accreditation review. Use “Approve onboarding” above to
                  register the DID and activate this Trusted Issuer.
                </span>
              </div>`
            : null}
          ${failed
            ? html`<div class="row" style="padding:14px 20px;border-top:1px solid var(--md-sys-color-outline-variant)">
                <span class="badge badge--bad">${icon("flag")} ${STATE_LABEL[status.state]}</span>
                ${status.kybResult
                  ? html`<div class="muted" style="margin-top:8px;font-size:12px">
                      ${JSON.stringify(status.kybResult)}
                    </div>`
                  : null}
              </div>`
            : null}
        </div>
      </div>
    `;
  }

  private renderSteps(done: number, failed: boolean) {
    let lastPhase = "";
    return html`${TI_STEPS.map((s) => {
      const isDone = s.n <= done && !failed;
      const isCurrent = s.n === done + 1 && !failed && done < 8;
      const phaseHeader =
        s.phase !== lastPhase ? html`<div class="phase">${s.phase}</div>` : null;
      lastPhase = s.phase;
      return html`
        ${phaseHeader}
        <div class="step ${isDone ? "step--done" : ""} ${isCurrent ? "step--current" : ""}">
          <span class="step__num">
            ${isDone ? icon("check_circle") : s.n}
          </span>
          <div>
            <div class="step__title">${s.title}</div>
            <div class="step__desc">${s.desc}</div>
            <div class="artifacts">
              ${s.artifacts.map((a) => html`<span class="chip">${a}</span>`)}
            </div>
          </div>
        </div>
      `;
    })}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ebsi-onboarding-page": EbsiOnboardingPage;
  }
}
